import { Download, ExternalLink } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Select } from '@/shared/components/ui/Select';
import { QuotationWorkspacePanel } from '@/features/section5/QuotationWorkspacePanel';
import { selectedQuotationWorkspaceId } from '@/features/section5/panelSelection';
import { PrintableDocument } from './PrintableDocument';
import { canPreview, detectFileType, deriveFileName, isImage, isPdf } from '../constants';
import type { SalesDoc, SalesDocFolder } from '../types';

interface SalesDocDrawerProps {
  doc: SalesDoc | null;
  canManage: boolean;
  canRecordPayment?: boolean;
  deleting?: boolean;
  folders?: SalesDocFolder[];
  onMoveToFolder?: (doc: SalesDoc, folderId: string | null) => void;
  onClose: () => void;
  onEdit: (doc: SalesDoc) => void;
  onDelete: (doc: SalesDoc) => void;
  onRecordPayment?: (doc: SalesDoc) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 border-b border-outline py-2 last:border-0">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className="text-right text-data-md text-on-surface">{value}</span>
    </div>
  );
}

function fmtDate(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtINR(n?: number | null) {
  if (n === undefined || n === null) return '';
  return `₹${n.toLocaleString('en-IN')}`;
}

// Client-approved audit fix — same intent as SalesDocsPage.tsx's Status
// column tone mapping (kept independent here since that one is typed `any`),
// plus the new 'partially_paid' value. Uses BadgeTone's real values —
// 'danger', not the 'critical' alias that page bypasses via `any`.
const STATUS_TONE: Record<string, BadgeTone> = {
  draft: 'neutral',
  sent: 'primary',
  accepted: 'success',
  finalized: 'success',
  paid: 'success',
  partially_paid: 'warning',
  rejected: 'danger',
  expired: 'danger',
  overdue: 'danger',
};

function statusLabel(s: string) {
  return s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function SalesDocDrawer({ doc, canManage, canRecordPayment, deleting, folders, onMoveToFolder, onClose, onEdit, onDelete, onRecordPayment }: SalesDocDrawerProps) {
  const ft = detectFileType(doc?.url);
  const Icon = ft.icon;
  const quotationWorkspaceId = selectedQuotationWorkspaceId(doc);

  return (
    <Drawer open={!!doc} onClose={onClose} title={doc?.title ?? 'Document'}>
      {doc && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`flex h-9 w-9 items-center justify-center rounded-full bg-surface-variant ${ft.tone === 'danger' ? 'text-danger' : 'text-on-surface-variant'}`}>
              <Icon className="h-5 w-5" />
            </span>
            <Badge tone={ft.tone}>{ft.label}</Badge>
          </div>

          {doc.description && <p className="whitespace-pre-wrap text-data-md text-on-surface">{doc.description}</p>}

          {/* Amount / balance / status — client-approved audit fix. Shown
              whenever this doc carries an amount (invoices, and anything else
              that was given one); `amountDue` is invoice-specific (see
              types.ts) so the balance row only appears once the backend has
              a value for it. */}
          {(doc.amount || doc.status) && (
            <div>
              {!!doc.amount && <Row label="Amount" value={fmtINR(doc.amount)} />}
              {doc.amountDue !== undefined && doc.amountDue !== null && (
                <Row label="Balance Due" value={fmtINR(doc.amountDue)} />
              )}
              {doc.status && (
                <Row label="Status" value={<Badge tone={STATUS_TONE[doc.status] || 'neutral'}>{statusLabel(doc.status)}</Badge>} />
              )}
            </div>
          )}

          <div>
            <Row label="File" value={deriveFileName(doc.url) || '—'} />
            <Row label="Uploaded By" value={doc.creator_name} />
            <Row label="Uploaded" value={fmtDate(doc.created_at)} />
          </div>

          {canManage && folders && onMoveToFolder && (
            <div>
              <label className="mb-1 block text-body-sm text-on-surface-variant">Folder</label>
              <Select
                name="doc-folder"
                value={doc.folderId || ''}
                onChange={(e) => onMoveToFolder(doc, e.target.value || null)}
                options={[{ value: '', label: 'Unfiled' }, ...folders.map((f) => ({ value: f.id, label: f.name }))]}
              />
            </div>
          )}

          {quotationWorkspaceId && <QuotationWorkspacePanel salesDocId={quotationWorkspaceId} />}

          {/* Preview only for images / PDFs / Generated Documents */}
          {doc.is_generated && doc.template_data ? (
            <div className="w-full overflow-hidden border border-on-surface rounded-lg scale-75 origin-top-left max-h-[400px] overflow-y-auto bg-card mb-[-25%]">
              <PrintableDocument doc={doc} />
            </div>
          ) : doc.url && canPreview(doc.url) && (
            <div>
              <h3 className="mb-2 text-label-caps uppercase text-on-surface-variant">Preview</h3>
              {isImage(doc.url) ? (
                <a href={doc.url} target="_blank" rel="noreferrer">
                  <img src={doc.url} alt={doc.title} className="max-h-72 w-full rounded-card border border-outline object-contain" />
                </a>
              ) : isPdf(doc.url) ? (
                <iframe title={doc.title} src={doc.url} className="h-80 w-full rounded-card border border-outline" />
              ) : null}
            </div>
          )}

          {/* Actions. Two distinct "generated" shapes exist here: the older
              template_data-driven one (rendered inline via PrintableDocument
              above, printed via window.print()) and documents auto-logged
              from a real Quotation/Proforma/Invoice (is_generated with a
              real `url` pointing at the live document route, no
              template_data) — those need an Open link same as a manually
              uploaded file, just not a raw file Download. */}
          {doc.url && !doc.template_data ? (
            <div className="flex gap-2">
              <a href={doc.url} target="_blank" rel="noreferrer" className="flex-1">
                <Button variant="outline" className="w-full"><ExternalLink className="h-4 w-4" /> Open</Button>
              </a>
              {!doc.is_generated && (
                <a href={doc.url} download={deriveFileName(doc.url)}>
                  <Button variant="outline"><Download className="h-4 w-4" /> Download</Button>
                </a>
              )}
            </div>
          ) : !doc.url && !doc.is_generated ? (
            <p className="text-body-sm text-on-surface-variant">No document link attached.</p>
          ) : null}

          {doc.is_generated && doc.template_data && (
            <div className="flex gap-2">
              <Button variant="outline" className="w-full" onClick={() => window.print()}>Print Document</Button>
            </div>
          )}

          {canRecordPayment && onRecordPayment && (
            <div className="flex gap-2 border-t border-outline pt-3">
              <Button onClick={() => onRecordPayment(doc)} className="w-full bg-success text-white hover:bg-success/90">
                Record Payment
              </Button>
            </div>
          )}

          {canManage && (
            <div className="flex gap-2 border-t border-outline pt-3">
              <Button onClick={() => onEdit(doc)} className="flex-1">Edit</Button>
              <Button variant="danger" loading={deleting} onClick={() => onDelete(doc)}>Delete</Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
